/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'

export const _8 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>8 - Медиазапросы (Media Queries)</h2>
    <p className={styles.description}>
      <b>Медиазапросы</b> — это модуль CSS3, который позволяет создавать отзывчивые веб-сайты, используя различные
      правила стилей для разных устройств или типов носителей. В зависимости от этих правил содержимое отображается в
      соответствии с различными условиями, например размером экрана, окна браузера или самого устройства, а также
      ориентацией и разрешением устройства.{' '}
    </p>
    <p className={styles.description}>
      В общем случае медиа-запрос состоит из ключевого слова, описывающего тип устройства (необязательный параметр) и
      выражения, проверяющего характеристики данного устройства. Из всех характеристик чаще всего проверяется ширина
      устройства width. Медиа-запрос является логическим выражением, которое возвращает истину или ложь.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.1 - Способы добавления медиазапросов</h3>
      <p className={styles.description}>
        Существует несколько способов добавления медиазапросов к странице, появлялись они последовательно, с развитием
        отзывчивого веб-дизайна. Отличаются лишь в имплементации, результат работы будет одинаков.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>8.1.1 - В HTML файле</h4>
        <p className={styles.description}>
          Создается файл стилей и наполняется css правилами. После чего подключается к странице, используя тег link, к
          которому добавляется атрибут media. Отметим, что сам файл стилей будет загружен вместе со страницей, не
          зависимо от ширины экрана устройства. Но стили из него будут применены только при выполнении условий media.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>8.1.2 - Внутри таблицы стилей</h4>
        <p className={styles.description}>
          Создается файл стилей. Внутри него, используя специальную конструкцию @media, описываются стили для
          определенных ширин экрана. В примере ниже при ширине экрана 768px и выше цвет левой колонки изменится.{' '}
        </p>
        <p className={styles.description}>
          В современной разработке значительно чаще используется этот метод. Мы прописываем стили элементам интерфейса
          под определенные медиаправила, и они применяются в зависимости от условий.{' '}
        </p>
        <iframe
          height="300"
          scrolling="no"
          title="rotman_html_1_9_1"
          src="https://codepen.io/rotmanyan/embed/MvpXZK?default-tab=html%2Cresult&editable=true&theme-id=dark"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
        <p className={styles.notificationWarn}>
          <b>@media (rules)</b> - используется для применения стилей на определенных носителях, размерах экрана и т.д.
          Это позволяет нам указать различные варианты дизайна в зависимости от размера экрана. Затем страницу можно
          оптимизировать и показывать совершенно по-разному для мобильных телефонов, планшетов и разных размеров окон
          браузера.{' '}
        </p>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.2 - min-width vs max-width</h3>
      <p className={styles.description}>Давайте разберемся с min-width и max-width в rules у медиа-запроса. </p>
      <p className={styles.description}>
        <code className={styles.code}>@media (min-width: 500px)</code> - говорит о том, что стили, которые указаны
        внутри этого медиа-правила, будут применены при минимальной ширине экрана 500px и шире.{' '}
      </p>
      <p className={styles.description}>
        <code className={styles.code}>@media (max-width: 500px)</code> - говорит о том, что стили, которые указаны
        внутри этого медиа-правила, будут применены от нуля до максимальной ширины экрана в 500px.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.3 - Логические операторы</h3>
      <p className={styles.description}>
        Логичным будет вопрос, а что делать, если я хочу чтобы какое-то css-правило применилось только в диапазоне, к
        примеру, от 400px до 800px? Или наоборот, применялось всегда кроме диапазона 400px-800px? Для этого существуют
        логические операторы. Мы рассмотрим самые распространенные.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>8.3.1 - Операторы and и not</h4>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            <code className={styles.code}>and</code> - связывает между собой разные условия. Код ниже сделает так, что
            левая колонка будет окрашиваться в желтый цвет в диапазоне 400px - 800px.
          </li>
          <li className={styles.list__item}>
            <code className={styles.code}>not</code> - позволяет сработать медиазапросу в противоположном случае.
            Ключевое слово not добавляется в начало медиазапроса и применяется ко всему запросу целиком.
          </li>
        </ul>
        <iframe
          height="300"
          scrolling="no"
          title="rotman_html_1_9_1"
          src="https://codepen.io/rotmanyan/embed/OjpwLG?default-tab=html%2Cresult&editable=true&theme-id=dark"
          frameBorder="no"
          loading="lazy"
          allowTransparency="true"
          allowFullScreen="true"
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.4 - Типы устройств</h3>
      <p className={styles.description}>
        Что это за screen? Это один из типов устройств, всего их 4. Этот параметр не является обязательным и служит для
        более специфичного применения медиаправила.{' '}
      </p>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Тип</th>
            <th>Описание</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>all</td>
            <td>Подходит для всех типов устройств</td>
          </tr>
          <tr>
            <td>print</td>
            <td>
              Предназначен для страничных материалов и документов, просматриваемых на экране в режиме предварительного
              просмотра печати.
            </td>
          </tr>
          <tr>
            <td>screen</td>
            <td>Предназначен, в первую очередь для экранов цветных компьютерных мониторов.</td>
          </tr>
          <tr>
            <td>speech</td>
            <td>Предназначен для синтезаторов речи.</td>
          </tr>
        </tbody>
      </table>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.5 - Характеристики носителя</h3>
      <p className={styles.description}>
        К характеристикам медианосителя относятся проверяемые параметры устройства. Значения, которые используются при
        задании характеристик, являются контрольными точками. Такие как max-device-aspect-ratio, max-device-width,
        min-width, min-resolution и многие другие.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.6 - Device Orientation</h3>
      <p className={styles.description}>
        Если вам необходимо применить CSS в зависимости от ориентации браузера, вам помогут следующие правила. Это может
        быть особенно полезно для мобильных устройств.{' '}
      </p>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>ориентация</th>
            <th>правило</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Горизонтальная (landscape)</td>
            <td>{'@media screen and (orientation: landscape) {...}'}</td>
          </tr>
          <tr>
            <td>Вертикальная (portrait)</td>
            <td>{'@media screen and (orientation: portrait) {...}'} </td>
          </tr>
        </tbody>
      </table>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.7 - Pixel Ratio</h3>
      <p className={styles.description}>
        Важно иметь в виду, что пиксель CSS не должен быть таким же, как физический пиксель. Хотя экран может физически
        иметь ширину 720 пикселей, браузер может фактически применять CSS, предполагая, что он имеет ширину 480
        пикселей. Это сделано затем, чтобы стандартная веб-страница с большей вероятностью подошла под экран. В этом
        примере соотношение пикселей равно 1,5:1 - на каждый пиксель CSS есть 1½ физических пикселя. Стандартный
        настольный монитор будет иметь соотношение пикселей 1:1 - один пиксель CSS к каждому физическому пикселю.{' '}
      </p>
      <p className={styles.description}>
        Это media будет применено к экранам с соотношением пикселей 2:1. Также можно использовать min-device-pixel-ratio
        и max-device-pixel-ratio.{' '}
      </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_1_9_1"
        src="https://codepen.io/rotmanyan/embed/yoMqoM?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.8 - Несколько медиаправил</h3>
      <p className={styles.description}>
        Вы можете применить сколько угодно правил @media, поэтому у вас может быть несколько разных дизайнов (макетов),
        зависящих от размера экрана.{' '}
      </p>
      <p className={styles.description}>Данные медиаправила будут изменять фон body в зависимости от ширины экрана. </p>
      <iframe
        height="300"
        scrolling="no"
        title="rotman_html_1_9_1"
        src="https://codepen.io/rotmanyan/embed/ZJejja?default-tab=html%2Cresult&editable=true&theme-id=dark"
        frameBorder="no"
        loading="lazy"
        allowTransparency="true"
        allowFullScreen="true"
      />
      <p className={styles.description}>Итак, алгоритм записи медиаправила: </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>Указываем @media и выражение проверки ширины экрана устройства </li>
        <li className={styles.list__item}>Внутри media указываем css-правило для селектора </li>
        <li className={styles.list__item}>
          Внутри css-правила указываем css-свойства и их значения, которые мы хотим применить при этом media{' '}
        </li>
      </ul>
      <p className={styles.notificationWarn}>
        <ul className={styles.list}>
          <li className={styles.list__item}>
            Мы можем применить сколько угодно media в файле стилей, главное следить за выражениями проверки ширины
            экрана. Чтобы не было конфликтов.{' '}
          </li>
          <li className={styles.list__item}>
            Внутри каждого media нет ограничения на количество css-правил, мы можем написать сколько угодно css правил
            для разных селекторов внутри одного media.{' '}
          </li>
          <li className={styles.list__item}>
            css-правила наследуются. Это значит, правила написаные при{' '}
            <code className={styles.code}>min-width: 480px</code>, будут применяться и при{' '}
            <code className={styles.code}>min-width: 768px</code>.{' '}
          </li>
        </ul>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>8.9 - Как узнать какие прописывать медиаправила?</h3>
      <p className={styles.description}>
        При составлении медиазапросов нужно ориентироваться на так называемые переломные (контрольные) точки дизайна,
        т.е. такие значения ширины области просмотра, в которых дизайн сайта существенно меняется, например, изменяется
        положение элементов интерфейса. Для верстальщика это те точки, макеты для которых ему предоставили.{' '}
      </p>
      <p className={styles.description}>
        Под каждую контрольную точку рисуется отдельный макет. Между контрольными точками, верстка должна быть
        «резиновой». Чтобы адаптировать дизайн сайта под разные устройства, необходимо задать разные стили для разных
        разрешений экранов, используя определённые контрольные точки.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>8.9.1 - Распространенные переломные точки</h4>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ширина</th>
              <th>устройство</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>320px</td>
              <td>Мобильные телефоны с узким экраном</td>
            </tr>
            <tr>
              <td>480px</td>
              <td>Современные смартфоны среднего\высокого уровня</td>
            </tr>
            <tr>
              <td>768px</td>
              <td>Таблетки</td>
            </tr>
            <tr>
              <td>960px</td>
              <td>Таблетки с широким экраном</td>
            </tr>
            <tr>
              <td>1200px</td>
              <td>Ноутбуки</td>
            </tr>
            <tr>
              <td>1440px</td>
              <td>Десктопы</td>
            </tr>
          </tbody>
        </table>
      </section>
    </section>
  </section>
))
